import { useConfig } from '@travelwin/core';
import { useTranslation } from 'react-i18next';

import { useDeviceOS } from '@/hooks/useDeviceOS';

import { useAnalytics } from '../../analytics';
import AppStoreBadge from './AppStoreButton.svg';
import GooglePlayBadge from './GooglePlayButton.svg';
import { useCreateStoreLink } from './useCreateStoreLink';

const MobileStoreButton = ({
  label,
  url,
  badgeImage,
  onClick,
}: {
  label: string;
  url: string;
  badgeImage: string;
  onClick?: () => void;
}) => {
  return (
    <a
      aria-label={label}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      <img src={badgeImage} alt="" loading="lazy" />
    </a>
  );
};

type Props = {
  onClick?: () => void;
};

export const AppStore = ({ onClick }: Props) => {
  const { APPLE_STORE_APP_URL } = useConfig();
  const storeButtonUrl = useCreateStoreLink();
  const { isMobileWeb } = useDeviceOS();
  const { t } = useTranslation();
  const analyticsContext = useAnalytics();

  const handleAppStoreClicked = () => {
    analyticsContext?.trackClickAction({
      element_id: 'btn_app_store',
      element_text: 'Apple App Store',
    });

    if (onClick) {
      onClick();
    }
  };

  if (!APPLE_STORE_APP_URL) {
    return null;
  }

  const storeUrl = isMobileWeb
    ? storeButtonUrl ?? APPLE_STORE_APP_URL
    : APPLE_STORE_APP_URL;

  return (
    <MobileStoreButton
      label={t('store.appStore', 'App Store')}
      url={storeUrl}
      badgeImage={AppStoreBadge}
      onClick={handleAppStoreClicked}
    />
  );
};

export const GooglePlayStore = ({ onClick }: Props) => {
  const { GOOGLE_PLAY_APP_URL } = useConfig();
  const storeButtonUrl = useCreateStoreLink();
  const { isMobileWeb } = useDeviceOS();
  const { t } = useTranslation();
  const analyticsContext = useAnalytics();

  const handleGooglePlayStoreClicked = () => {
    analyticsContext?.trackClickAction({
      element_id: 'btn_play_store',
      element_text: 'Google Play Store',
    });

    if (onClick) {
      onClick();
    }
  };

  if (!GOOGLE_PLAY_APP_URL) {
    return null;
  }

  const storeUrl = isMobileWeb
    ? storeButtonUrl ?? GOOGLE_PLAY_APP_URL
    : GOOGLE_PLAY_APP_URL;

  return (
    <MobileStoreButton
      label={t('store.googlePlay', 'Google Play')}
      url={storeUrl}
      badgeImage={GooglePlayBadge}
      onClick={handleGooglePlayStoreClicked}
    />
  );
};
